import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message
} from 'antd';
import {
  UserOutlined,
  CloudServerOutlined,
  DollarOutlined,
  RiseOutlined,
  TeamOutlined,
  TrophyOutlined,
  FireOutlined,
  ReloadOutlined,
  LinkOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ApiOutlined
} from '@ant-design/icons';
import { statsAPI, serverAPI, userCenterAPI, apiControlAPI } from '../services/api';
import { Line, Bar } from 'react-chartjs-2';
import { PageHeader, StatCard, StatCardGrid, LoadingOverlay, EmptyState } from '../components/ui';
import { lineChartOptions, barChartOptions, CHART_COLORS } from '../styles/chart-config';
import { formatFlowAmount } from '../utils/displayMoney';

const { Text } = Typography;

/**
 * 表格单元格组件
 */
const TableCell = memo(({ primary, secondary, meta }) => (
  <div className="table-cell">
    <div className="table-cell-primary">{primary}</div>
    {secondary && <div className="table-cell-secondary">{secondary}</div>}
    {meta && <div className="table-cell-meta">{meta}</div>}
  </div>
));

const USER_CENTER_KIND_LABELS = {
  page: '页面',
  anchor: '锚点',
  api: '接口',
  link: '外链'
};

const USER_CENTER_AUTH_LABELS = {
  none: '不带参',
  append_auth: '自动带登录态'
};

/**
 * 控制台页面
 */
const Dashboard = memo(function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [serverStatus, setServerStatus] = useState([]);
  const [registrationTrend, setRegistrationTrend] = useState([]);
  const [paymentTrend, setPaymentTrend] = useState([]);
  const [userCenterLinks, setUserCenterLinks] = useState([]);
  const [userCenterLoading, setUserCenterLoading] = useState(false);
  const [apiEndpoints, setApiEndpoints] = useState([]);
  const [apiControlLoading, setApiControlLoading] = useState(false);
  const [apiKeyword, setApiKeyword] = useState('');
  const [featureModalOpen, setFeatureModalOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [savingFeature, setSavingFeature] = useState(false);
  const [featureForm] = Form.useForm();

  const loadUserCenterLinks = useCallback(async () => {
    setUserCenterLoading(true);
    try {
      const response = await userCenterAPI.getLinks();
      setUserCenterLinks(response.data?.data || []);
    } catch (error) {
      console.error('Load user center links error:', error);
    } finally {
      setUserCenterLoading(false);
    }
  }, []);

  const loadApiControls = useCallback(async () => {
    setApiControlLoading(true);
    try {
      const response = await apiControlAPI.getEndpoints();
      setApiEndpoints(response.data?.data || []);
    } catch (error) {
      console.error('Load API controls error:', error);
    } finally {
      setApiControlLoading(false);
    }
  }, []);

  // 加载数据
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, serverRes, trendRes, paymentRes] = await Promise.all([
        statsAPI.getOverview(),
        serverAPI.getStatus(),
        statsAPI.getRegistrationTrend(),
        statsAPI.getPaymentTrend()
      ]);

      setOverview(overviewRes.data?.data || {});
      setServerStatus(serverRes.data?.data?.servers || []);
      setRegistrationTrend(trendRes.data?.data || []);
      setPaymentTrend(paymentRes.data?.data || []);
    } catch (error) {
      console.error('Load dashboard data error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadUserCenterLinks();
    loadApiControls();
  }, [loadData, loadUserCenterLinks, loadApiControls]);

  const openFeatureModal = useCallback((record = null) => {
    setEditingFeature(record);
    featureForm.setFieldsValue({
      linkKey: record?.linkKey || '',
      title: record?.title || '',
      description: record?.description || '',
      targetUrl: record?.targetUrl || '',
      kind: record?.kind || 'page',
      authMode: record?.authMode || 'append_auth',
      openMode: record?.openMode || 'same',
      sortOrder: Number(record?.sortOrder || 0),
      enabled: record ? Boolean(record.enabled) : true
    });
    setFeatureModalOpen(true);
  }, [featureForm]);

  const closeFeatureModal = useCallback(() => {
    setFeatureModalOpen(false);
    setEditingFeature(null);
    featureForm.resetFields();
  }, [featureForm]);

  const saveFeature = useCallback(async () => {
    setSavingFeature(true);
    try {
      const values = await featureForm.validateFields();
      const payload = {
        ...values,
        sortOrder: Number(values.sortOrder || 0),
        enabled: Boolean(values.enabled)
      };

      if (editingFeature?.id) {
        await userCenterAPI.updateLink(editingFeature.id, payload);
      } else {
        await userCenterAPI.createLink(payload);
      }
      message.success('用户中心功能已保存');
      closeFeatureModal();
      loadUserCenterLinks();
    } catch (error) {
      if (!error?.errorFields) {
        message.error('保存用户中心功能失败');
      }
    } finally {
      setSavingFeature(false);
    }
  }, [closeFeatureModal, editingFeature, featureForm, loadUserCenterLinks]);

  const toggleFeatureEnabled = useCallback(async (record, enabled) => {
    try {
      await userCenterAPI.updateLink(record.id, {
        ...record,
        enabled
      });
      message.success(enabled ? '功能已启用' : '功能已隐藏');
      loadUserCenterLinks();
    } catch (error) {
      message.error('更新功能状态失败');
    }
  }, [loadUserCenterLinks]);

  const deleteFeature = useCallback((record) => {
    Modal.confirm({
      title: '删除用户中心功能',
      content: `确认删除「${record.title || record.linkKey}」吗？删除后玩家端不再显示这个入口。`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await userCenterAPI.deleteLink(record.id);
          message.success('用户中心功能已删除');
          loadUserCenterLinks();
        } catch (error) {
          message.error('删除用户中心功能失败');
          throw error;
        }
      }
    });
  }, [loadUserCenterLinks]);

  const toggleApiEndpoint = useCallback(async (record, enabled) => {
    if (record.locked && !enabled) {
      message.warning('这个API是后台保底入口，不能关闭');
      return;
    }

    try {
      const response = await apiControlAPI.updateEndpoint(record.id, { enabled });
      const updated = response.data?.data;
      setApiEndpoints((items) => items.map((item) => (
        item.id === record.id ? { ...item, ...(updated || {}), enabled } : item
      )));
      message.success(enabled ? 'API已开启' : 'API已关闭，访问会返回404');
    } catch (error) {
      message.error('API状态更新失败');
    }
  }, []);

  const syncApiControls = useCallback(async () => {
    setApiControlLoading(true);
    try {
      const response = await apiControlAPI.syncEndpoints();
      setApiEndpoints(response.data?.data || []);
      message.success('API列表已同步');
    } catch (error) {
      message.error('API列表同步失败');
    } finally {
      setApiControlLoading(false);
    }
  }, []);

  // 图表数据
  const regChartData = useMemo(() => ({
    labels: registrationTrend.map(item => item.date),
    datasets: [{
      label: '新增玩家',
      data: registrationTrend.map(item => item.count),
      borderColor: CHART_COLORS.primary,
      backgroundColor: CHART_COLORS.primaryAlpha,
      fill: true,
      tension: 0.4
    }]
  }), [registrationTrend]);

  const payChartData = useMemo(() => ({
    labels: paymentTrend.map(item => item.date),
    datasets: [{
      label: '充值金额',
      data: paymentTrend.map(item => item.total),
      backgroundColor: CHART_COLORS.warningAlpha,
      borderColor: CHART_COLORS.warning,
      borderWidth: 1
    }]
  }), [paymentTrend]);

  // 表格列配置
  const serverColumns = useMemo(() => [
    {
      title: '服务器名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <TableCell
          primary={text || '未命名服务器'}
          secondary={`端口 ${record.port || '-'}`}
          meta={record.isRunning ? '当前节点运行中' : '当前节点未启动'}
        />
      )
    },
    {
      title: '状态',
      key: 'isRunning',
      width: 100,
      render: (_, record) => (
        <Tag color={record.isRunning ? 'success' : 'error'}>
          {record.isRunning ? '运行中' : '已停止'}
        </Tag>
      )
    },
    {
      title: 'CPU',
      key: 'cpu',
      width: 120,
      render: (_, record) => (
        <TableCell
          primary={record.isRunning ? `${(record.cpuUsage || 0).toFixed(1)}%` : '-'}
          secondary="CPU 占用"
        />
      )
    },
    {
      title: '内存',
      key: 'memory',
      width: 120,
      render: (_, record) => (
        <TableCell
          primary={record.isRunning ? `${record.memoryUsage || 0} MB` : '-'}
          secondary="内存占用"
        />
      )
    }
  ], []);

  const userCenterColumns = useMemo(() => [
    {
      title: '功能',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <TableCell
          primary={text || record.linkKey}
          secondary={record.description || '用户中心入口'}
          meta={`Key: ${record.linkKey}`}
        />
      )
    },
    {
      title: '目标',
      dataIndex: 'targetUrl',
      key: 'targetUrl',
      render: (value, record) => (
        <TableCell
          primary={<Text code>{value || '-'}</Text>}
          secondary={`${USER_CENTER_KIND_LABELS[record.kind] || record.kind || '页面'} / ${USER_CENTER_AUTH_LABELS[record.authMode] || record.authMode || '默认'}`}
          meta={record.openMode === 'new' ? '新窗口打开' : '当前窗口打开'}
        />
      )
    },
    {
      title: '显示',
      key: 'enabled',
      width: 96,
      render: (_, record) => (
        <Switch
          size="small"
          checked={Boolean(record.enabled)}
          checkedChildren="显示"
          unCheckedChildren="隐藏"
          onChange={(checked) => toggleFeatureEnabled(record, checked)}
        />
      )
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80,
      render: (value) => value ?? 0
    },
    {
      title: '操作',
      key: 'action',
      width: 130,
      render: (_, record) => (
        <Space size={4}>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openFeatureModal(record)}>
            编辑
          </Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => deleteFeature(record)}>
            删除
          </Button>
        </Space>
      )
    }
  ], [deleteFeature, openFeatureModal, toggleFeatureEnabled]);

  const filteredApiEndpoints = useMemo(() => {
    const keyword = apiKeyword.trim().toLowerCase();
    if (!keyword) {
      return apiEndpoints;
    }
    return apiEndpoints.filter((item) => (
      String(item.endpointKey || '').toLowerCase().includes(keyword) ||
      String(item.pathPattern || '').toLowerCase().includes(keyword) ||
      String(item.groupName || '').toLowerCase().includes(keyword) ||
      String(item.method || '').toLowerCase().includes(keyword)
    ));
  }, [apiEndpoints, apiKeyword]);

  const apiClosedCount = useMemo(
    () => apiEndpoints.filter((item) => !item.enabled).length,
    [apiEndpoints]
  );

  const apiControlColumns = useMemo(() => [
    {
      title: 'API',
      dataIndex: 'pathPattern',
      key: 'pathPattern',
      render: (value, record) => (
        <TableCell
          primary={
            <Space size={8} wrap>
              <Tag color={record.method === 'GET' ? 'blue' : record.method === 'POST' ? 'green' : 'purple'}>
                {record.method}
              </Tag>
              <Text code>{value}</Text>
            </Space>
          }
          secondary={record.endpointKey}
          meta={record.groupName}
        />
      )
    },
    {
      title: '访问状态',
      key: 'enabled',
      width: 120,
      render: (_, record) => (
        <Switch
          size="small"
          checked={Boolean(record.enabled)}
          disabled={Boolean(record.locked)}
          checkedChildren="开启"
          unCheckedChildren="关闭"
          onChange={(checked) => toggleApiEndpoint(record, checked)}
        />
      )
    },
    {
      title: '保护',
      key: 'locked',
      width: 100,
      render: (_, record) => (
        record.locked ? <Tag color="gold">不可关闭</Tag> : <Tag color="default">可关闭</Tag>
      )
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (value) => value ? new Date(value).toLocaleString('zh-CN') : '-'
    }
  ], [toggleApiEndpoint]);

  // 统计卡片数据
  const statCards = useMemo(() => [
    {
      label: '总玩家数',
      value: overview?.totalPlayers?.toLocaleString() || 0,
      icon: <UserOutlined />,
      color: '#2563eb'
    },
    {
      label: '在线玩家',
      value: overview?.onlinePlayers?.toLocaleString() || 0,
      icon: <CloudServerOutlined />,
      color: '#16a34a'
    },
    {
      label: '今日活跃',
      value: overview?.activeToday?.toLocaleString() || 0,
      icon: <TeamOutlined />,
      color: '#7c3aed'
    },
    {
      label: '今日新增',
      value: overview?.newPlayersToday?.toLocaleString() || 0,
      icon: <RiseOutlined />,
      color: '#ea580c',
      trend: overview?.newPlayersChange
    },
    {
      label: '今日收入',
      value: formatFlowAmount(overview?.revenueToday || 0),
      icon: <DollarOutlined />,
      color: '#db2777',
      suffix: '元',
      trend: overview?.revenueChange
    },
    {
      label: '泡点VIP',
      value: overview?.vipPlayers?.toLocaleString() || 0,
      icon: <TrophyOutlined />,
      color: '#ca8a04'
    },
    {
      label: '平均等级',
      value: overview?.avgLevel || 0,
      icon: <FireOutlined />,
      color: '#dc2626'
    },
    {
      label: '服务器数',
      value: serverStatus.length || overview?.totalServers || 0,
      icon: <CloudServerOutlined />,
      color: '#0891b2'
    }
  ], [overview, serverStatus.length]);

  if (loading) {
    return <LoadingOverlay tip="加载控制台数据..." />;
  }

  return (
    <div className="page-container">
      <PageHeader
        title="控制台"
        description="实时监控全区服运营数据，快速了解当前状态"
        tags={[
          { text: '实时监控', color: 'blue' },
          { text: `${serverStatus.length} 台服务器`, color: 'default' }
        ]}
        actions={
          <Button icon={<ReloadOutlined />} onClick={() => { loadData(); loadUserCenterLinks(); loadApiControls(); }}>
            刷新数据
          </Button>
        }
      />

      {/* 统计卡片网格 */}
      <StatCardGrid columns={4}>
        {statCards.map((card, index) => (
          <StatCard key={index} {...card} />
        ))}
      </StatCardGrid>

      <Card
        className="card-section"
        title={
          <Space>
            <ApiOutlined />
            <span>API开关管理</span>
          </Space>
        }
        extra={
          <Space wrap>
            <Tag color={apiClosedCount > 0 ? 'red' : 'green'}>已关闭 {apiClosedCount} 项</Tag>
            <Input.Search
              allowClear
              placeholder="搜索路径、分组或方法"
              value={apiKeyword}
              onChange={(event) => setApiKeyword(event.target.value)}
              style={{ width: 260 }}
            />
            <Button icon={<ReloadOutlined />} onClick={syncApiControls}>
              同步API
            </Button>
          </Space>
        }
      >
        <Table
          columns={apiControlColumns}
          dataSource={filteredApiEndpoints}
          loading={apiControlLoading}
          pagination={{ pageSize: 12, showSizeChanger: true }}
          scroll={{ x: 900, y: 420 }}
          rowKey="id"
          locale={{
            emptyText: (
              <EmptyState
                title="暂无API配置"
                description="点击同步API后会读取当前后端路由并生成开关"
                type="default"
              />
            )
          }}
        />
      </Card>

      {/* 图表区域 */}
      <Row gutter={[20, 20]}>
        <Col xs={24} lg={12}>
          <Card
            className="card-section"
            title="近 7 日新增玩家趋势"
            extra={<Text type="secondary">按天查看新进账号变化</Text>}
          >
            <div className="chart-panel" style={{ height: 280 }}>
              {registrationTrend.length > 0 ? (
                <Line data={regChartData} options={lineChartOptions('近 7 日新增玩家趋势')} />
              ) : (
                <EmptyState
                  title="暂无新增趋势数据"
                  description="等统计接口返回数据后，这里会自动显示最近 7 日曲线"
                  type="default"
                />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            className="card-section"
            title="近 7 日充值趋势"
            extra={<Text type="secondary">按天查看充值金额波动</Text>}
          >
            <div className="chart-panel" style={{ height: 280 }}>
              {paymentTrend.length > 0 ? (
                <Bar data={payChartData} options={barChartOptions('近 7 日充值趋势')} />
              ) : (
                <EmptyState
                  title="暂无充值趋势数据"
                  description="等支付统计同步后，这里会自动显示最近 7 日柱状图"
                  type="default"
                />
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* 服务器状态表格 */}
      <Card
        className="card-section"
        title="全区服状态"
        extra={
          <Space>
            <Text type="secondary">共 {serverStatus.length} 台</Text>
            <Tag color="blue">实时刷新依赖接口</Tag>
          </Space>
        }
      >
        <Table
          columns={serverColumns}
          dataSource={serverStatus}
          pagination={false}
          size="middle"
          scroll={{ x: 600, y: 320 }}
          rowKey="id"
          locale={{
            emptyText: (
              <EmptyState
                title="暂无服务器状态数据"
                description="当前未获取到可展示的区服节点信息"
                type="default"
              />
            )
          }}
        />
      </Card>

      <Card
        className="card-section"
        title={
          <Space>
            <LinkOutlined />
            <span>用户中心功能配置</span>
          </Space>
        }
        extra={
          <Space>
            <Tag color="blue">当前 {userCenterLinks.length} 项</Tag>
            <Tag color={userCenterLinks.some((item) => item.enabled) ? 'green' : 'orange'}>
              {userCenterLinks.some((item) => item.enabled) ? '有启用项' : '全部隐藏'}
            </Tag>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openFeatureModal()}>
              新增功能
            </Button>
          </Space>
        }
      >
        <Table
          columns={userCenterColumns}
          dataSource={userCenterLinks}
          loading={userCenterLoading}
          pagination={false}
          scroll={{ x: 860 }}
          rowKey="id"
          locale={{
            emptyText: (
              <EmptyState
                title="暂无用户中心功能"
                description="先新增功能入口，再在用户中心页面里自动展示。"
                type="default"
              />
            )
          }}
        />
      </Card>

      <Modal
        title={editingFeature ? '编辑用户中心功能' : '新增用户中心功能'}
        open={featureModalOpen}
        onCancel={closeFeatureModal}
        onOk={saveFeature}
        confirmLoading={savingFeature}
        okText="保存"
        cancelText="取消"
        width={720}
        destroyOnClose
      >
        <Form form={featureForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="功能键" name="linkKey" rules={[{ required: true, message: '请输入功能键' }]}>
                <Input placeholder="promotion / shop / gift / custom_xxx" disabled={Boolean(editingFeature)} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="标题" name="title" rules={[{ required: true, message: '请输入标题' }]}>
                <Input placeholder="例如 全民推广" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="说明" name="description">
            <Input.TextArea rows={2} placeholder="简短描述这个入口做什么" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="目标地址" name="targetUrl" rules={[{ required: true, message: '请输入目标地址' }]}>
                <Input placeholder="/shop.html / #promotion / /api/configs/public/client-update" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="类型" name="kind" rules={[{ required: true, message: '请选择类型' }]}>
                <Select
                  options={[
                    { value: 'page', label: '页面' },
                    { value: 'anchor', label: '锚点' },
                    { value: 'api', label: '接口' },
                    { value: 'link', label: '外链' }
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="打开方式" name="openMode">
                <Select
                  options={[
                    { value: 'same', label: '当前页' },
                    { value: 'new', label: '新窗口' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="鉴权模式" name="authMode">
                <Select
                  options={[
                    { value: 'none', label: '不带登录态' },
                    { value: 'append_auth', label: '自动追加 authKey' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="排序" name="sortOrder">
                <InputNumber min={0} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="是否显示" name="enabled" valuePropName="checked">
            <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
});

export default Dashboard;
